import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, DollarSign, Percent, BedDouble, TrendingUp, TrendingDown, Building2, FolderArchive } from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const usd2 = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => `${Number(v || 0).toFixed(1)}%`;

function Delta({ v }) {
  if (v == null) return <span className="text-xs text-muted-foreground">—</span>;
  const up = v >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(v)}%
    </span>
  );
}

function Compliance({ c, pid }) {
  if (!c || c.status === "none") return <span className="text-xs text-muted-foreground" title="No licenses tracked">—</span>;
  const green = c.status === "green";
  const parts = [];
  if (c.expired) parts.push(`${c.expired} expired`);
  if (c.expiring) parts.push(`${c.expiring} expiring`);
  if (c.missing) parts.push(`${c.missing} missing`);
  return (
    <Link to={`/properties/${pid}?tab=licenses`} className="inline-flex items-center gap-1.5" title={green ? "All licenses current" : parts.join(", ")} data-testid={`compliance-${pid}`}>
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${green ? "bg-emerald-500" : "bg-red-500 animate-pulse"}`} />
      <span className={`text-xs font-semibold ${green ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
        {green ? "Current" : `${c.issues} issue${c.issues > 1 ? "s" : ""}`}
      </span>
    </Link>
  );
}

export default function Portfolio() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPid, setSelectedPid] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/owner-dashboard", { params: { month, years: 3 } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data?.properties?.length && !data.properties.some((p) => p.property_id === selectedPid)) {
      setSelectedPid(data.properties[0].property_id);
    }
  }, [data, selectedPid]);

  const years = data?.years || [];
  const curYear = data?.year;
  const prevYear = curYear - 1;
  const moName = data ? MONTHS[data.selected_month - 1] : "";
  const propRev = data?.property_revenue?.[selectedPid];
  const selName = data?.properties?.find((p) => p.property_id === selectedPid)?.name || "";

  // portfolio totals for selected month (this vs last year)
  const totThis = (data?.properties || []).reduce((a, p) => ({
    revenue: a.revenue + Number(p.this.revenue || 0),
  }), { revenue: 0 });
  const totLast = (data?.properties || []).reduce((a, p) => ({
    revenue: a.revenue + Number(p.last.revenue || 0),
  }), { revenue: 0 });
  const revChange = totLast.revenue ? Math.round((totThis.revenue - totLast.revenue) / totLast.revenue * 1000) / 10 : null;

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Portfolio</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Owner Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">Year-over-year performance and 3-year revenue trend.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="portfolio-month" />
          <Link to="/documents"><Button variant="outline" data-testid="portfolio-docs-link"><FolderArchive className="mr-2 h-4 w-4" /> Documents</Button></Link>
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : !data || data.properties.length === 0 ? (
        <Card className="rounded-none border p-12 text-center">
          <Building2 className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No properties assigned yet.</p>
        </Card>
      ) : (
        <>
          {/* Portfolio revenue YoY */}
          <Card className="rounded-none border p-6" data-testid="portfolio-yoy">
            <div className="label-caps text-xs text-muted-foreground">{moName} Portfolio Revenue — Year over Year</div>
            <div className="mt-3 flex flex-wrap items-end gap-8">
              <div>
                <div className="text-xs text-muted-foreground">{curYear}</div>
                <div className="font-heading text-3xl font-black text-primary lg:text-4xl" data-testid="yoy-this">{usd(totThis.revenue)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">{prevYear}</div>
                <div className="font-heading text-2xl font-bold text-muted-foreground">{usd(totLast.revenue)}</div>
              </div>
              <div className="pb-1"><Delta v={revChange} /></div>
            </div>
          </Card>

          {/* Per-property YoY metrics for selected month */}
          <Card className="rounded-none border" data-testid="property-yoy-table">
            <div className="border-b border-border px-4 py-3 font-heading text-sm font-bold">{moName} {curYear} vs {prevYear} — by Property</div>
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead className="text-right">Revenue {curYear}</TableHead>
                    <TableHead className="text-right">Revenue {prevYear}</TableHead>
                    <TableHead className="text-right">Δ</TableHead>
                    <TableHead className="text-right">ADR {curYear}/{String(prevYear).slice(2)}</TableHead>
                    <TableHead className="text-right">Occ% {curYear}/{String(prevYear).slice(2)}</TableHead>
                    <TableHead className="text-right">RevPAR {curYear}/{String(prevYear).slice(2)}</TableHead>
                    <TableHead className="text-center">Compliance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.properties.map((p) => (
                    <TableRow key={p.property_id} data-testid={`portfolio-row-${p.property_id}`}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-right font-mono">{usd2(p.this.revenue)}</TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">{usd2(p.last.revenue)}</TableCell>
                      <TableCell className="text-right"><Delta v={p.change.revenue} /></TableCell>
                      <TableCell className="text-right font-mono text-xs">{usd2(p.this.adr)} <span className="text-muted-foreground">/ {usd2(p.last.adr)}</span></TableCell>
                      <TableCell className="text-right font-mono text-xs">{pct(p.this.occupancy)} <span className="text-muted-foreground">/ {pct(p.last.occupancy)}</span></TableCell>
                      <TableCell className="text-right font-mono text-xs">{usd2(p.this.revpar)} <span className="text-muted-foreground">/ {usd2(p.last.revpar)}</span></TableCell>
                      <TableCell className="text-center"><Compliance c={p.compliance} pid={p.property_id} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* Per-property 3-year revenue (select a property on top) */}
          <Card className="rounded-none border" data-testid="revenue-3yr-table">
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
              <span className="mr-2 font-heading text-sm font-bold">{selName} Revenue — {years[0]}–{years[years.length - 1]}</span>
              <div className="flex flex-wrap gap-1.5">
                {data.properties.map((p) => (
                  <button
                    key={p.property_id}
                    onClick={() => setSelectedPid(p.property_id)}
                    data-testid={`prop-select-${p.property_id}`}
                    className={`rounded-none border px-2.5 py-1 text-xs font-medium transition-colors ${
                      p.property_id === selectedPid
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-secondary text-muted-foreground hover:border-primary hover:text-foreground"
                    }`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    {years.map((y) => (<TableHead key={y} className="text-right">{y}</TableHead>))}
                    <TableHead className="text-right">YoY</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(propRev?.revenue_table || []).map((row) => {
                    const cur = row.values[years[years.length - 1]] || 0;
                    const prev = row.values[years[years.length - 2]] || 0;
                    const ch = prev ? Math.round((cur - prev) / prev * 1000) / 10 : null;
                    return (
                      <TableRow key={row.month} className={row.month === data.selected_month ? "bg-accent/40" : ""}>
                        <TableCell className="font-medium">{MONTHS[row.month - 1]}</TableCell>
                        {years.map((y) => (
                          <TableCell key={y} className="text-right font-mono text-xs">{row.values[y] ? usd(row.values[y]) : "—"}</TableCell>
                        ))}
                        <TableCell className="text-right"><Delta v={ch} /></TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="border-t-2 border-border bg-secondary font-semibold">
                    <TableCell className="label-caps">Total</TableCell>
                    {years.map((y) => (<TableCell key={y} className="text-right font-mono">{usd(propRev?.year_totals?.[String(y)])}</TableCell>))}
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
